import { useEffect, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string;
      theme: 'dark';
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => void;
    },
  ) => string;
  remove: (widgetId: string) => void;
}

type TurnstileWindow = Window & { turnstile?: TurnstileApi };

let loading: Promise<TurnstileApi> | null = null;

/** Load Cloudflare's script once, only when a form actually needs it. */
function loadTurnstile(): Promise<TurnstileApi> {
  const existing = (window as TurnstileWindow).turnstile;
  if (existing) return Promise.resolve(existing);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      const api = (window as TurnstileWindow).turnstile;
      if (api) resolve(api);
      else reject(new Error('Turnstile did not initialise'));
    };
    script.onerror = () => {
      loading = null; // allow another attempt
      reject(new Error('Turnstile failed to load'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

export interface TurnstileProps {
  onToken: (token: string | null) => void;
}

/**
 * The Cloudflare Turnstile challenge, for deployments that set `VITE_TURNSTILE_SITE_KEY`. Without a
 * key it renders nothing and the form works as before (the API only demands a token when it has a
 * secret configured). The widget is dark-themed to match the page.
 */
export function Turnstile({ onToken }: TurnstileProps) {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim();
  const hostRef = useRef<HTMLDivElement | null>(null);
  const latest = useRef(onToken);
  useEffect(() => {
    latest.current = onToken;
  });

  useEffect(() => {
    const host = hostRef.current;
    if (!siteKey || !host) return undefined;
    let widgetId: string | null = null;
    let disposed = false;
    void loadTurnstile()
      .then((api) => {
        if (disposed) return;
        widgetId = api.render(host, {
          sitekey: siteKey,
          theme: 'dark',
          callback: (token) => {
            latest.current(token);
          },
          'expired-callback': () => {
            latest.current(null);
          },
          'error-callback': () => {
            latest.current(null);
          },
        });
      })
      .catch(() => {
        latest.current(null); // the form still submits; the server decides
      });
    return () => {
      disposed = true;
      if (widgetId) (window as TurnstileWindow).turnstile?.remove(widgetId);
    };
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={hostRef} className="turnstile" />;
}
